<script setup lang="ts">
import UIToolTip from '@/components/ui/UIToolTip.vue'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import { TTooltipAlignment, TTooltipDirection } from '@lifk/types'
import { PhQuestion } from '@phosphor-icons/vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** What text should be displayed in the button's tooltip? (The tooptip will not be displayed here, only in the actual application.) */
    tooltipText: string
    /** In which direction should the tooltip appear with respect to the button? */
    tooltipDirection: TTooltipDirection
    /** How should the tooltip be aligned with respect to the button? */
    tooltipAlignment: TTooltipAlignment
    /** What text size should be used in the tooltip? */
    tooltipTextSize?: 'auto' | 'small' | 'default'
    /** What accessible label should be used, instead of the tooltip text? */
    label?: string
    /** What variant of the button should be used? */
    variant?: keyof typeof variantMapping
    /** Is the button disabled? */
    disabled?: boolean
    /** What HTML control type should be applied to the button? */
    buttonType?: 'button' | 'submit'
    /** Does the button have a popup (e.g. a menu)? (Used to set aria attributes) */
    hasPopup?: boolean
    /** If the button has a popup, is it currently expanded? (Used to set aria attributes) */
    expanded?: boolean
    /** The id of the element this button controls (sets aria-controls) */
    ariaControls?: string
  }>(),
  {
    variant: 'default',
    buttonType: 'button'
  }
)

const variantMapping = {
  default: '',
  extraPadding: 'p-2',
  uiButtonBar: 'px-[6px] py-[6px]',
  outlineSafariPatch: 'p-1 -outline-offset-4 focus:outline focus:outline-2' // use for elements that do not show focus ring in Safari
}

const isMobile = computed(() => useDetectBreakpoint('xs'))
const hovered = ref(false)
const pressed = ref(false)

const iconWeight = computed<'duotone' | 'regular'>(() => {
  if (hovered.value && !props.disabled) return 'duotone'
  return 'regular'
})

const onMouseLeave = () => {
  hovered.value = false
  pressed.value = false
}
</script>

<template>
  <UIToolTip
    :text="tooltipText"
    :direction="tooltipDirection"
    :alignment="tooltipAlignment"
    :text-size="tooltipTextSize"
    :disabled="disabled || isMobile"
    class="group rounded"
  >
    <div>
      <button
        class="relative block text-emphasis rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        :class="variantMapping[variant]"
        :disabled="disabled"
        :type="buttonType"
        :aria-label="label ?? tooltipText"
        :aria-haspopup="hasPopup"
        :aria-expanded="expanded"
        :aria-controls="ariaControls"
        @mouseenter="hovered = true"
        @mouseleave="onMouseLeave"
        @mousedown="pressed = true"
        @mouseup="pressed = false"
      >
        <slot
          :hovered="hovered"
          :pressed="pressed"
          :default-icon-weight="iconWeight"
        >
          <PhQuestion size="20" :weight="iconWeight" />
        </slot>
      </button>
    </div>
  </UIToolTip>
</template>
